import { Link } from 'react-router-dom';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Treemap,
} from 'recharts';

import {
  BarRow,
  PartRowList,
  Widget,
  formatDollars,
} from '@/components/dashboard/Widget';
import { cn } from '@/lib/cn';
import {
  type WidgetId,
  useDashboardActivity,
  useDashboardAdmin,
  useDashboardBom,
  useDashboardCatalog,
  useDashboardInventory,
  useDashboardQuotas,
  useDashboardSourcing,
} from '@/lib/dashboard';

interface WidgetProps {
  orgSlug: string;
}

const partLink = (_orgSlug: string) => (pn: string, id: number) =>
  `/parts?q=${encodeURIComponent(pn)}&openId=${id}`;

// ===== Inventory ===========================================================

function InventoryValue({ orgSlug }: WidgetProps) {
  const q = useDashboardInventory(orgSlug);
  return (
    <Widget title="Inventory totals" loading={q.isPending} error={q.error}>
      {q.data && (
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-2xl font-semibold">{q.data.total_parts.toLocaleString()}</div>
            <div className="text-xs text-stone-500 dark:text-stone-400">parts</div>
          </div>
          <div>
            <div className="text-2xl font-semibold">{q.data.total_units.toLocaleString()}</div>
            <div className="text-xs text-stone-500 dark:text-stone-400">units</div>
          </div>
          <div>
            <div className="text-2xl font-semibold">{formatDollars(q.data.total_value_cents)}</div>
            <div className="text-xs text-stone-500 dark:text-stone-400">value</div>
          </div>
        </div>
      )}
    </Widget>
  );
}

function LowStock({ orgSlug }: WidgetProps) {
  const q = useDashboardInventory(orgSlug);
  return (
    <Widget
      title="Low stock"
      subtitle={q.data ? `${q.data.low_stock.length}` : undefined}
      loading={q.isPending}
      error={q.error}
      actionHref="/parts?low_stock=1"
      actionLabel="See all"
    >
      {q.data && (
        <PartRowList
          rows={q.data.low_stock}
          emptyText="Nothing below threshold."
          hrefBuilder={partLink(orgSlug)}
        />
      )}
    </Widget>
  );
}

function ZeroStock({ orgSlug }: WidgetProps) {
  const q = useDashboardInventory(orgSlug);
  return (
    <Widget
      title="Zero stock"
      subtitle={q.data ? `${q.data.zero_stock.length}` : undefined}
      loading={q.isPending}
      error={q.error}
    >
      {q.data && (
        <PartRowList
          rows={q.data.zero_stock}
          emptyText="No parts at zero."
          hrefBuilder={partLink(orgSlug)}
        />
      )}
    </Widget>
  );
}

function NeverCounted({ orgSlug }: WidgetProps) {
  const q = useDashboardInventory(orgSlug);
  return (
    <Widget
      title="Never counted"
      subtitle={q.data ? `${q.data.never_counted.length}` : undefined}
      loading={q.isPending}
      error={q.error}
    >
      {q.data && (
        <PartRowList
          rows={q.data.never_counted}
          emptyText="Every part has been counted at least once."
          hrefBuilder={partLink(orgSlug)}
        />
      )}
    </Widget>
  );
}

function StaleCount({ orgSlug }: WidgetProps) {
  const q = useDashboardInventory(orgSlug);
  return (
    <Widget
      title="Stale counts"
      subtitle="not counted in 90+ days"
      loading={q.isPending}
      error={q.error}
    >
      {q.data && (
        <PartRowList
          rows={q.data.stale_count}
          emptyText="No stale counts."
          hrefBuilder={partLink(orgSlug)}
        />
      )}
    </Widget>
  );
}

function NoLocation({ orgSlug }: WidgetProps) {
  const q = useDashboardInventory(orgSlug);
  return (
    <Widget
      title="Stock without a location"
      subtitle={q.data ? `${q.data.no_location.length}` : undefined}
      loading={q.isPending}
      error={q.error}
    >
      {q.data && (
        <PartRowList
          rows={q.data.no_location}
          emptyText="Every stocked part has a home."
          hrefBuilder={partLink(orgSlug)}
        />
      )}
    </Widget>
  );
}

function ValueByCategory({ orgSlug }: WidgetProps) {
  const q = useDashboardInventory(orgSlug);
  const data =
    q.data?.value_by_category
      .filter((r) => r.value_cents > 0)
      .map((r) => ({
        name: r.label,
        size: r.value_cents,
        // Carried so the cell renderer can show the formatted dollar
        // amount as the secondary line in the box.
        valueCents: r.value_cents,
      })) ?? [];
  return (
    <Widget
      title="$ by category"
      span="lg:col-span-2"
      loading={q.isPending}
      error={q.error}
    >
      {q.data && data.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">No priced inventory yet.</p>
      )}
      {data.length > 0 && (
        <div className="h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={data}
              dataKey="size"
              stroke="#fff"
              fill="#ea580c"
              isAnimationActive={false}
              content={<TreemapCell formatValue={formatDollars} />}
            />
          </ResponsiveContainer>
        </div>
      )}
    </Widget>
  );
}

function InventoryByForm({ orgSlug }: WidgetProps) {
  const q = useDashboardInventory(orgSlug);
  const total = q.data?.by_form.reduce((s, r) => s + r.count, 0) ?? 0;
  return (
    <Widget title="Inventory by form" loading={q.isPending} error={q.error}>
      {q.data && q.data.by_form.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">
          No form set on any inventory row yet.
        </p>
      )}
      {q.data && q.data.by_form.length > 0 && (
        <div className="space-y-2">
          {q.data.by_form.map((r) => (
            <BarRow key={r.label} label={prettyForm(r.label)} count={r.count} total={total} />
          ))}
        </div>
      )}
    </Widget>
  );
}

function prettyForm(s: string): string {
  if (s === 'cut_tape') return 'Cut tape';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Custom Treemap cell: forge-orange rect, white label + secondary
 * line, with text hidden when the cell's too small to read. Recharts
 * passes geometry props (x/y/width/height) plus the data row's
 * `name` and our extra `valueCents`. */
function TreemapCell(props: {
  formatValue: (n: number) => string;
} & Record<string, unknown>) {
  const x = Number(props.x ?? 0);
  const y = Number(props.y ?? 0);
  const width = Number(props.width ?? 0);
  const height = Number(props.height ?? 0);
  const name = props.name as string | undefined;
  const valueCents = props.valueCents as number | undefined;
  // Skip the root-level rect that Recharts renders behind the cells.
  if ((props.depth as number | undefined) === 0) return null;
  // Forge-orange palette with a touch of variance so adjacent cells
  // are visually distinguishable. Using HSL keeps the brand color
  // stable but lets bigger boxes lean darker for hierarchy.
  const idx = Number(props.index ?? 0);
  const lightness = 50 - (idx % 6) * 4;
  const fill = `hsl(20, 80%, ${lightness}%)`;
  const showLabel = width > 50 && height > 28;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        stroke="rgba(255,255,255,0.85)"
        strokeWidth={1}
      />
      {showLabel && name && (
        <>
          <text
            x={x + 6}
            y={y + 14}
            fill="white"
            fontSize={11}
            fontWeight={600}
          >
            {name.length > Math.floor(width / 7)
              ? name.slice(0, Math.floor(width / 7) - 1) + '…'
              : name}
          </text>
          {valueCents !== undefined && height > 40 && (
            <text x={x + 6} y={y + 28} fill="rgba(255,255,255,0.85)" fontSize={10}>
              {props.formatValue(valueCents)}
            </text>
          )}
        </>
      )}
    </g>
  );
}

// ===== Sourcing ============================================================

function EolPrimary({ orgSlug }: WidgetProps) {
  const q = useDashboardSourcing(orgSlug);
  return (
    <Widget
      title="Parts with EOL primary"
      subtitle={q.data ? `${q.data.eol_primary.length}` : undefined}
      loading={q.isPending}
      error={q.error}
    >
      {q.data && (
        <PartRowList
          rows={q.data.eol_primary}
          emptyText="No primaries flagged EOL."
          hrefBuilder={partLink(orgSlug)}
        />
      )}
    </Widget>
  );
}

function NoSupplier({ orgSlug }: WidgetProps) {
  const q = useDashboardSourcing(orgSlug);
  return (
    <Widget
      title="No supplier"
      subtitle={q.data ? `${q.data.no_supplier.length}` : undefined}
      loading={q.isPending}
      error={q.error}
    >
      {q.data && (
        <PartRowList
          rows={q.data.no_supplier}
          emptyText="Every part has at least one supplier."
          hrefBuilder={partLink(orgSlug)}
        />
      )}
    </Widget>
  );
}

function NoPrice({ orgSlug }: WidgetProps) {
  const q = useDashboardSourcing(orgSlug);
  return (
    <Widget
      title="Primary supplier with no price"
      subtitle={q.data ? `${q.data.no_price.length}` : undefined}
      loading={q.isPending}
      error={q.error}
    >
      {q.data && (
        <PartRowList
          rows={q.data.no_price}
          emptyText="Every primary supplier has a price."
          hrefBuilder={partLink(orgSlug)}
        />
      )}
    </Widget>
  );
}

function StaleSync({ orgSlug }: WidgetProps) {
  const q = useDashboardSourcing(orgSlug);
  return (
    <Widget
      title="Stale supplier sync"
      subtitle="not synced in 30+ days"
      loading={q.isPending}
      error={q.error}
    >
      {q.data && (
        <PartRowList
          rows={q.data.stale_sync}
          emptyText="All primaries synced recently."
          hrefBuilder={partLink(orgSlug)}
        />
      )}
    </Widget>
  );
}

function DistributorMix({ orgSlug }: WidgetProps) {
  const q = useDashboardSourcing(orgSlug);
  const total = q.data?.distributor_mix.reduce((s, r) => s + r.count, 0) ?? 0;
  return (
    <Widget title="Distributor mix" loading={q.isPending} error={q.error}>
      {q.data && q.data.distributor_mix.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">No suppliers yet.</p>
      )}
      {q.data && q.data.distributor_mix.length > 0 && (
        <div className="space-y-2">
          {q.data.distributor_mix.map((r) => (
            <BarRow key={r.label} label={r.label} count={r.count} total={total} />
          ))}
        </div>
      )}
    </Widget>
  );
}

function DistributorQuotas({ orgSlug }: WidgetProps) {
  const q = useDashboardQuotas(orgSlug);
  return (
    <Widget
      title="Distributor quotas (today)"
      subtitle="API calls used"
      loading={q.isPending}
      error={q.error}
    >
      {q.data && q.data.rows.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">
          No distributor calls yet today.
        </p>
      )}
      {q.data && q.data.rows.length > 0 && (
        <div className="space-y-2">
          {q.data.rows.map((r) => {
            const pct = r.daily_limit > 0 ? (r.used_today / r.daily_limit) * 100 : 0;
            const danger = pct > 80;
            return (
              <div key={r.distributor_slug}>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-stone-700 dark:text-stone-300">{r.distributor_name}</span>
                  <span className="font-mono text-stone-500 dark:text-stone-400">
                    {r.used_today.toLocaleString()} / {r.daily_limit.toLocaleString()}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100 dark:bg-stone-800">
                  <div
                    className={cn(
                      'h-full rounded-full',
                      danger ? 'bg-red-500' : 'bg-forge-500',
                    )}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Widget>
  );
}

function LeadTimeOutliers({ orgSlug }: WidgetProps) {
  const q = useDashboardSourcing(orgSlug);
  return (
    <Widget
      title="Lead time outliers"
      subtitle="primary lead > 30 days"
      loading={q.isPending}
      error={q.error}
    >
      {q.data && q.data.lead_time_outliers.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">All primaries lead ≤ 30 days.</p>
      )}
      {q.data && q.data.lead_time_outliers.length > 0 && (
        <ul className="space-y-1">
          {q.data.lead_time_outliers.slice(0, 8).map((r) => (
            <li key={r.part_id} className="flex justify-between gap-3">
              <Link
                to={partLink(orgSlug)(r.part_number, r.part_id)}
                className="font-mono text-forge-700 dark:text-forge-400 hover:underline"
              >
                {r.part_number}
              </Link>
              <span className="text-xs text-stone-500 dark:text-stone-400">
                {r.distributor_name} · {r.lead_time_days}d
              </span>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}

// ===== Catalog =============================================================

function CatalogTotals({ orgSlug }: WidgetProps) {
  const q = useDashboardCatalog(orgSlug);
  return (
    <Widget title="Catalog" loading={q.isPending} error={q.error}>
      {q.data && (
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-2xl font-semibold">{q.data.total_parts.toLocaleString()}</div>
            <div className="text-xs text-stone-500 dark:text-stone-400">total parts</div>
          </div>
          <div>
            <div className="text-2xl font-semibold text-green-700 dark:text-green-400">
              {q.data.active_parts.toLocaleString()}
            </div>
            <div className="text-xs text-stone-500 dark:text-stone-400">active</div>
          </div>
          <div>
            <div className="text-2xl font-semibold text-stone-500 dark:text-stone-400">
              {q.data.obsolete_parts.toLocaleString()}
            </div>
            <div className="text-xs text-stone-500 dark:text-stone-400">obsolete</div>
          </div>
        </div>
      )}
    </Widget>
  );
}

function PartsByCategory({ orgSlug }: WidgetProps) {
  const q = useDashboardCatalog(orgSlug);
  const data =
    q.data?.by_category.map((r) => ({
      name: r.label,
      size: r.count,
      valueCents: r.count, // reused as "primary value" for the cell label
    })) ?? [];
  return (
    <Widget
      title="Parts by category"
      span="lg:col-span-2"
      loading={q.isPending}
      error={q.error}
    >
      {q.data && data.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">No categorized parts.</p>
      )}
      {data.length > 0 && (
        <div className="h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={data}
              dataKey="size"
              stroke="#fff"
              fill="#ea580c"
              isAnimationActive={false}
              content={
                <TreemapCell formatValue={(n) => n.toLocaleString()} />
              }
            />
          </ResponsiveContainer>
        </div>
      )}
    </Widget>
  );
}

function PartsByMounting({ orgSlug }: WidgetProps) {
  const q = useDashboardCatalog(orgSlug);
  const total = q.data?.by_mounting.reduce((s, r) => s + r.count, 0) ?? 0;
  return (
    <Widget title="Parts by mounting" loading={q.isPending} error={q.error}>
      {q.data && q.data.by_mounting.length > 0 && (
        <div className="space-y-2">
          {q.data.by_mounting.map((r) => (
            <BarRow key={r.label} label={r.label} count={r.count} total={total} />
          ))}
        </div>
      )}
    </Widget>
  );
}

function PartsByLifecycle({ orgSlug }: WidgetProps) {
  const q = useDashboardCatalog(orgSlug);
  const total = q.data?.by_lifecycle.reduce((s, r) => s + r.count, 0) ?? 0;
  return (
    <Widget title="Parts by lifecycle" loading={q.isPending} error={q.error}>
      {q.data && q.data.by_lifecycle.length > 0 && (
        <div className="space-y-2">
          {q.data.by_lifecycle.map((r) => (
            <BarRow key={r.label} label={r.label} count={r.count} total={total} />
          ))}
        </div>
      )}
    </Widget>
  );
}

function Uncategorized({ orgSlug }: WidgetProps) {
  const q = useDashboardCatalog(orgSlug);
  return (
    <Widget title="Uncategorized" loading={q.isPending} error={q.error}>
      {q.data && (
        <div className="text-center">
          <div className="text-3xl font-semibold">{q.data.uncategorized_count}</div>
          <div className="text-xs text-stone-500 dark:text-stone-400">parts with no category</div>
        </div>
      )}
    </Widget>
  );
}

function RohsCoverage({ orgSlug }: WidgetProps) {
  const q = useDashboardCatalog(orgSlug);
  if (!q.data) {
    return <Widget title="RoHS coverage" loading={q.isPending} error={q.error}>{null}</Widget>;
  }
  const total = q.data.rohs_compliant + q.data.rohs_noncompliant + q.data.rohs_unknown;
  return (
    <Widget title="RoHS coverage" loading={q.isPending} error={q.error}>
      <div className="space-y-2">
        <BarRow label="Compliant" count={q.data.rohs_compliant} total={total} />
        <BarRow label="Non-compliant" count={q.data.rohs_noncompliant} total={total} />
        <BarRow label="Unknown" count={q.data.rohs_unknown} total={total} />
      </div>
    </Widget>
  );
}

function KicadCoverage({ orgSlug }: WidgetProps) {
  const q = useDashboardCatalog(orgSlug);
  if (!q.data) {
    return <Widget title="KiCad coverage" loading={q.isPending} error={q.error}>{null}</Widget>;
  }
  const tracked = q.data.kicad_tracked;
  const total = q.data.kicad_total;
  return (
    <Widget title="KiCad coverage" loading={q.isPending} error={q.error}>
      <div className="space-y-2">
        <BarRow label="Tracked" count={tracked} total={total} />
        <BarRow label="With 3D model" count={q.data.kicad_with_3d} total={total} />
        <BarRow label="Untracked" count={total - tracked} total={total} />
      </div>
    </Widget>
  );
}

// ===== Activity ============================================================

function RecentActivity({ orgSlug }: WidgetProps) {
  const q = useDashboardActivity(orgSlug);
  return (
    <Widget title="Recent activity" span="lg:col-span-2" loading={q.isPending} error={q.error}>
      {q.data && q.data.recent_activity.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">No activity yet.</p>
      )}
      {q.data && q.data.recent_activity.length > 0 && (
        <ul className="space-y-1">
          {q.data.recent_activity.map((r) => (
            <li key={r.part_id} className="flex justify-between gap-3 truncate">
              <Link
                to={partLink(orgSlug)(r.part_number, r.part_id)}
                className="min-w-0 truncate hover:underline"
              >
                <span className="font-mono text-forge-700 dark:text-forge-400">{r.part_number}</span>
                {r.description && (
                  <span className="ml-2 text-stone-600 dark:text-stone-400">{r.description}</span>
                )}
              </Link>
              <span className="shrink-0 text-xs text-stone-500 dark:text-stone-400">
                {new Date(r.updated_at).toLocaleDateString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}

function RecentEol({ orgSlug }: WidgetProps) {
  const q = useDashboardActivity(orgSlug);
  return (
    <Widget title="Recent EOL events" subtitle="last 30 days" loading={q.isPending} error={q.error}>
      {q.data && q.data.recent_eol.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">No recent EOL flags.</p>
      )}
      {q.data && q.data.recent_eol.length > 0 && (
        <ul className="space-y-1">
          {q.data.recent_eol.slice(0, 8).map((r) => (
            <li key={`${r.part_id}-${r.eol_detected_at ?? ''}`} className="flex justify-between gap-3">
              <Link
                to={partLink(orgSlug)(r.part_number, r.part_id)}
                className="font-mono text-forge-700 dark:text-forge-400 hover:underline"
              >
                {r.part_number}
              </Link>
              <span className="text-xs text-stone-500 dark:text-stone-400">
                {r.distributor_name}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}

function RecentPriceChanges({ orgSlug }: WidgetProps) {
  const q = useDashboardActivity(orgSlug);
  return (
    <Widget title="Recent price changes" subtitle="last 30 days" loading={q.isPending} error={q.error}>
      {q.data && q.data.recent_price_changes.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">No price moves in the window.</p>
      )}
      {q.data && q.data.recent_price_changes.length > 0 && (
        <ul className="space-y-2">
          {q.data.recent_price_changes.slice(0, 6).map((r) => {
            const up = r.pct_change > 0;
            const series = r.series.map((cents, i) => ({ i, cents }));
            return (
              <li
                key={`${r.part_id}-${r.changed_at}`}
                className="grid grid-cols-[minmax(0,1fr)_60px_56px] items-center gap-2"
              >
                <Link
                  to={partLink(orgSlug)(r.part_number, r.part_id)}
                  className="min-w-0 truncate font-mono text-forge-700 dark:text-forge-400 hover:underline"
                >
                  {r.part_number}
                </Link>
                <div className="h-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                      <Line
                        type="monotone"
                        dataKey="cents"
                        stroke={up ? '#dc2626' : '#15803d'}
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <span
                  className={cn(
                    'text-right font-mono text-xs',
                    up ? 'text-red-600' : 'text-green-700',
                  )}
                >
                  {up ? '+' : ''}
                  {r.pct_change.toFixed(1)}%
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Widget>
  );
}

// ===== Admin ===============================================================

function PendingAccountRequests({ orgSlug }: WidgetProps) {
  const q = useDashboardAdmin(orgSlug);
  return (
    <Widget
      title="Pending account requests"
      loading={q.isPending}
      error={q.error}
      actionHref={`/orgs/${orgSlug}`}
      actionLabel="Review"
    >
      {q.data && (
        <div className="text-center">
          <div className="text-3xl font-semibold">{q.data.pending_account_requests}</div>
          <div className="text-xs text-stone-500 dark:text-stone-400">awaiting review</div>
        </div>
      )}
    </Widget>
  );
}

function ActiveInvitations({ orgSlug }: WidgetProps) {
  const q = useDashboardAdmin(orgSlug);
  return (
    <Widget
      title="Active invitations"
      loading={q.isPending}
      error={q.error}
      actionHref={`/orgs/${orgSlug}`}
      actionLabel="Manage"
    >
      {q.data && (
        <div className="space-y-1 text-center">
          <div className="text-3xl font-semibold">{q.data.active_invitations}</div>
          <div className="text-xs text-stone-500 dark:text-stone-400">outstanding</div>
          {q.data.soonest_invite_expires_at && (
            <div className="text-xs text-stone-500 dark:text-stone-400">
              soonest expires {new Date(q.data.soonest_invite_expires_at).toLocaleDateString()}
            </div>
          )}
        </div>
      )}
    </Widget>
  );
}

// ===== BOM =================================================================

function BomTotals({ orgSlug }: WidgetProps) {
  const q = useDashboardBom(orgSlug);
  return (
    <Widget title="BOM totals" loading={q.isPending} error={q.error} actionHref="/boms" actionLabel="Open">
      {q.data && (
        <div className="grid grid-cols-2 gap-3 text-center">
          <div>
            <div className="text-2xl font-semibold">{q.data.total_boms}</div>
            <div className="text-xs text-stone-500 dark:text-stone-400">BOMs</div>
          </div>
          <div>
            <div className="text-2xl font-semibold">{q.data.total_lines}</div>
            <div className="text-xs text-stone-500 dark:text-stone-400">lines</div>
          </div>
        </div>
      )}
    </Widget>
  );
}

function CannotBuild({ orgSlug }: WidgetProps) {
  const q = useDashboardBom(orgSlug);
  return (
    <Widget
      title="Cannot build now"
      subtitle={q.data ? `${q.data.cannot_build.length}` : undefined}
      loading={q.isPending}
      error={q.error}
    >
      {q.data && q.data.cannot_build.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">Every BOM is buildable.</p>
      )}
      {q.data && q.data.cannot_build.length > 0 && (
        <ul className="space-y-1">
          {q.data.cannot_build.slice(0, 8).map((b) => (
            <li key={b.bom_id}>
              <Link
                to={`/boms/${b.bom_id}`}
                className="text-forge-700 dark:text-forge-400 hover:underline"
              >
                {b.bom_name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}

function AtRiskBoms({ orgSlug }: WidgetProps) {
  const q = useDashboardBom(orgSlug);
  return (
    <Widget
      title="At-risk BOMs"
      subtitle="contains EOL primary"
      loading={q.isPending}
      error={q.error}
    >
      {q.data && q.data.at_risk.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">No EOL primaries in any BOM.</p>
      )}
      {q.data && q.data.at_risk.length > 0 && (
        <ul className="space-y-1">
          {q.data.at_risk.slice(0, 8).map((b) => (
            <li key={b.bom_id}>
              <Link
                to={`/boms/${b.bom_id}`}
                className="text-forge-700 dark:text-forge-400 hover:underline"
              >
                {b.bom_name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}

function PopularParts({ orgSlug }: WidgetProps) {
  const q = useDashboardBom(orgSlug);
  return (
    <Widget title="Most-used parts" subtitle="by BOM-line count" loading={q.isPending} error={q.error}>
      {q.data && q.data.popular_parts.length === 0 && (
        <p className="text-stone-500 dark:text-stone-400">No BOM lines yet.</p>
      )}
      {q.data && q.data.popular_parts.length > 0 && (
        <ul className="space-y-1">
          {q.data.popular_parts.map((r) => (
            <li key={r.part_id} className="flex justify-between gap-3 truncate">
              <Link
                to={partLink(orgSlug)(r.part_number, r.part_id)}
                className="min-w-0 truncate font-mono text-forge-700 dark:text-forge-400 hover:underline"
              >
                {r.part_number}
              </Link>
              <span className="shrink-0 text-xs font-mono text-stone-500 dark:text-stone-400">
                ×{r.bom_appearances}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}

function MissingInventory({ orgSlug }: WidgetProps) {
  const q = useDashboardBom(orgSlug);
  return (
    <Widget
      title="Missing inventory"
      subtitle="in a BOM but zero stock"
      loading={q.isPending}
      error={q.error}
    >
      {q.data && (
        <PartRowList
          rows={q.data.missing_inventory}
          emptyText="Every BOM part has at least some stock."
          hrefBuilder={partLink(orgSlug)}
        />
      )}
    </Widget>
  );
}

// ===== Registry ============================================================

export const WIDGET_COMPONENTS: Record<
  WidgetId,
  (props: WidgetProps) => JSX.Element
> = {
  inventory_value: InventoryValue,
  low_stock: LowStock,
  zero_stock: ZeroStock,
  never_counted: NeverCounted,
  stale_count: StaleCount,
  no_location: NoLocation,
  value_by_category: ValueByCategory,
  inventory_by_form: InventoryByForm,
  eol_primary: EolPrimary,
  no_supplier: NoSupplier,
  no_price: NoPrice,
  stale_sync: StaleSync,
  distributor_mix: DistributorMix,
  distributor_quotas: DistributorQuotas,
  lead_time_outliers: LeadTimeOutliers,
  catalog_totals: CatalogTotals,
  parts_by_category: PartsByCategory,
  parts_by_mounting: PartsByMounting,
  parts_by_lifecycle: PartsByLifecycle,
  uncategorized: Uncategorized,
  rohs_coverage: RohsCoverage,
  kicad_coverage: KicadCoverage,
  recent_activity: RecentActivity,
  recent_eol: RecentEol,
  recent_price_changes: RecentPriceChanges,
  pending_account_requests: PendingAccountRequests,
  active_invitations: ActiveInvitations,
  bom_totals: BomTotals,
  cannot_build: CannotBuild,
  at_risk_boms: AtRiskBoms,
  popular_parts: PopularParts,
  missing_inventory: MissingInventory,
};
